import React, { useState, useEffect, useRef, useCallback } from 'react';

interface TextCarouselProps {
  texts: string[];
  interval?: number;
  className?: string;
  textClassName?: string;
  wrapperStyle?: React.CSSProperties;
}

const TextCarousel: React.FC<TextCarouselProps> = ({
  texts,
  interval = 3500,
  className = '',
  textClassName = '',
  wrapperStyle = {}
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Enhanced cycling logic with visibility detection
  const cycleText = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % texts.length);
  }, [texts.length]);

  // Start/stop based on visibility
  const startCycling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(cycleText, interval);
  }, [cycleText, interval]);

  const stopCycling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Initialize cycling
  useEffect(() => {
    startCycling();
    return stopCycling;
  }, [startCycling, stopCycling]);

  // Pause when not visible for performance
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          startCycling();
        } else {
          stopCycling();
        }
      },
      { threshold: 0.1 }
    );

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current);
    }

    return () => {
      observer.disconnect();
      stopCycling();
    };
  }, [startCycling, stopCycling]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopCycling();
      } else if (isVisible) {
        startCycling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isVisible, startCycling, stopCycling]);

  return (
    <>
      <style>
        {`
          .text-carousel-wrapper {
            position: relative;
            overflow: visible;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            min-height: 2.5em;
            height: auto;
            line-height: 1.2;
          }
          
          .text-carousel-item {
            position: absolute;
            width: 100%;
            left: 0;
            top: 50%;
            transform: translateY(-50%) translateY(30px) scale(0.95);
            transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            text-align: center;
            opacity: 0;
            will-change: transform, opacity;
            backface-visibility: hidden;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            white-space: nowrap;
          }
          
          @media (max-width: 640px) {
            .text-carousel-item {
              white-space: normal;
              word-wrap: break-word;
              hyphens: auto;
              font-size: 0.9em;
              line-height: 1.3;
              padding: 0 1rem;
            }
            
            .text-carousel-wrapper {
              min-height: 2.5em;
              overflow: visible;
            }
          }
          
          .text-carousel-item.active {
            opacity: 1;
            transform: translateY(-50%) scale(1);
          }
          
          .text-carousel-item.exiting {
            opacity: 0;
            transform: translateY(-50%) translateY(-30px) scale(0.95);
            transition-duration: 0.4s;
          }
          
          @media (prefers-reduced-motion: reduce) {
            .text-carousel-item {
              transition: opacity 0.3s ease;
              transform: none !important;
            }
            
            .text-carousel-item.active {
              transform: none !important;
            }
          }
        `}
      </style>
      
      <div 
        ref={wrapperRef}
        className={`text-carousel-wrapper ${className}`} 
        style={wrapperStyle}
      >
        {texts.map((text, index) => {
          const isActive = index === currentIndex;
          const isPrevious = index === (currentIndex - 1 + texts.length) % texts.length;
          
          return (
            <span
              key={`carousel-${index}`}
              className={`text-carousel-item ${isActive ? 'active' : ''} ${isPrevious ? 'exiting' : ''} ${textClassName}`}
              style={{
                zIndex: isActive ? 2 : isPrevious ? 1 : 0
              }}
            >
              {text}
            </span>
          );
        })}
      </div>
    </>
  );
};

export default TextCarousel;